
import {
  computed,
  defineComponent, onMounted, PropType, Ref, ref, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '@/store/mainStore';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import AppLabel from '@/components/AppLabel.vue';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
import DriveSharedBar from '@/modules/drive/components/DriveSharedBar.vue';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { BOTTOM_POPUP_TYPES, MEDIA_TYPES, PHOTO_VIEW_TYPE } from '@/types/enums';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import { IMedia } from '@/types/interfaces';

export default defineComponent({
  components: {
    AppBottomPopUp,
    AppLabel,
    AppLabelInput,
    ItemCase,
    DriveSharedBar,
  },
  props: {
    file: { type: Object as PropType<IMedia>, required: true },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();

    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const isEditLabels: Ref<boolean> = ref(false);

    const isPhoto = computed(() => mainStore.BottomPopUpType === BOTTOM_POPUP_TYPES.PHOTO_INFO);
    const isFile = computed(() => mainStore.BottomPopUpType === BOTTOM_POPUP_TYPES.FILE_INFO);
    const cases = computed(() => (isPhoto.value ? props.file?.whereUsed : props.file?.whereUsed) || []);
    const isShared = computed(() => props.file?.ownerId.internalId !== mainStore.user.accountId
      && props.file?.ownerId.internalId !== mainStore.boundCorporate.internalId);

    const isNetwork = computed(() => route.meta.isNetwork || false);

    const sharedTo = computed(() => {
      if (props.file?.share) {
        return props.file?.share.sharedAccounts.length + props.file?.share.sharedGroups.length;
      }
      return false;
    });

    const currentLabels = computed(() => {
      if (mainStore.inputPhotoViewTags.length) {
        return mainStore.inputPhotoViewTags;
      }
      if (isPhoto.value) {
        return mainStore.inputPhotoTags;
      }
      if (isFile.value) {
        return mainStore.inputFileTags;
      }
      return mainStore.inputCaseTags;
    });

    function hidePopUp() {
      emit('close');
    }

    function formatDate(date: number): string {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'long', day: '2-digit', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
      });
    }

    const fileOwner = computed(() => {
      if (isPhoto.value) {
        return props.file!.owner;
      }
      return props.file!.owner;
    });

    const authorPhotoUrl = computed(() => {
      if (isPhoto.value && props.file!.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.file!.owner.photo?.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if (!isPhoto.value && props.file!.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.file!.owner.photo?.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const createdDate = computed(() => {
      if (isPhoto.value) {
        return formatDate(props.file!.creationDate);
      }
      return formatDate(props.file!.creationDate);
    });
    const sharedDate = computed(() => {
      if (isPhoto.value && props.file?.sharedTime) {
        return formatDate(props.file?.sharedTime);
      }
      if (props.file?.sharedTime) {
        return formatDate(props.file?.sharedTime);
      }
      return '';
    });

    const mediaSize = computed(() => {
      const size = props.file?.size as number | undefined;
      if (!size) {
        return 'N/A';
      }
      if (size < 1024) {
        return `${size} B`;
      }
      if (size < 1024 * 1024) {
        const sizeInKB = (size / 1024).toFixed(2);
        return `${sizeInKB} KB`;
      }
      const sizeInMB = (size / (1024 * 1024)).toFixed(2);
      return `${sizeInMB} MB`;
    });
    const updatedDate = computed(() => ' no info');

    function addLabels() {
      if (isEditLabels.value) {
        driveStore.saveFileLabels(isPhoto.value);
      }
      isEditLabels.value = !isEditLabels.value;
    }

    function goToCase(caseId: string) {
      router.push(`/drive/case/my/${caseId}/view/?fromPhoto=${props.file?.id}`).then(() => {
        mainStore.BottomPopUpType = null;
      });
    }

    watch(() => props.file!.labels, () => {
      mainStore.lablesToTagsInput(props.file!.labels, true);
    });

    onMounted(() => {
      mainStore.lablesToTagsInput(isPhoto.value ? props.file!.labels : props.file!.labels, isFile.value, isPhoto.value);
    });

    return {
      mainStore,
      sharedTo,
      isEditLabels,
      isPhoto,
      isShared,
      createdDate,
      updatedDate,
      sharedDate,
      fileOwner,
      authorPhotoUrl,
      cases,
      hidePopUp,
      formatDate,
      addLabels,
      goToCase,
      isNetwork,
      currentLabels,
      mediaSize,
    };
  },
});
